<script lang="ts">
	import { tick } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { selectAll } from "#lib/contentEditable.js";

	interface Props {
		value: string | undefined;
		placeholder?: string;
		minLength?: number;
		maxLength?: number;
		allowEmpty?: boolean;
		tag?: keyof HTMLElementTagNameMap;
		title?: string;
		disabled?: boolean;
		dataActionName?: string;
		class?: string;
		id?: string;
		onKeydown?: (event: KeyboardEvent) => void;
		onFocus?: (event: FocusEvent) => void;
		onFocusout?: (event: FocusEvent) => void;
		onBlur?: (event: FocusEvent) => void;
		onChange?: (value: string | undefined) => void;
		onCommit?: (value: string | undefined) => void;
		onRevert?: (value: string | undefined) => void;
		[key: `data-${string}`]: unknown;
		[key: `aria-${string}`]: unknown;
	}

	const baseClass = "inline-block max-w-[--max-length] min-w-[--min-length] rounded empty:before:cursor-text empty:before:text-gray-500 empty:before:content-[attr(placeholder)] hover:border-none active:border-none active:outline-none";
	const focusClass = "hover:ring-primary-300 focus:ring-primary-500 hover:ring-1 hover:ring-offset-1 hover:outline-none focus:border-none focus:ring-2 focus:ring-offset-4 focus:outline-none";

	let { value = $bindable(), title, placeholder = "", minLength, maxLength, allowEmpty = false, tag: tag = "span", disabled = false, dataActionName, class: className, onKeydown, onFocus, onFocusout, onBlur, onChange, onCommit, onRevert, ...rest }: Props = $props();

	let originalValue: string | undefined = $state(value);
	let input: HTMLElement | undefined = $state();
	let isEditing = $state(false);

	$effect(() => {
		if (maxLength && (value?.length ?? -1) > maxLength) {
			value = value?.substring(0, maxLength - 1);
		}
	});

	$effect(() => {
		const text = value ?? "";
		if (input && !isEditing && input.innerText !== text) {
			// eslint-disable-next-line svelte/no-dom-manipulating
			input.innerText = text;
		}
	});

	function handleKeydown(event: KeyboardEvent) {
		changeValue(event);
		onKeydown?.(event);
	}

	function handleFocus(event: Event) {
		startEditing();
		onFocus?.(event as FocusEvent);
	}

	function handleFocusout(event: Event) {
		onFocusout?.(event as FocusEvent);
	}

	function handleInput() {
		value = input?.innerText;
		onChange?.(value);
	}

	function changeValue(event: KeyboardEvent) {
		const target = event.target as HTMLElement;
		switch (event.code) {
			case "Escape":
				event.preventDefault();
				if (input) {
					// eslint-disable-next-line svelte/no-dom-manipulating
					input.innerText = originalValue || "";
				}
				resetValue();
				target.blur();
				break;
			case "Enter":
			case "NumpadEnter":
				event.preventDefault();
				commitValue();
				target.blur();
				break;
			case "Backspace":
			case "Delete":
			case "ArrowLeft":
			case "ArrowRight":
			case "ArrowUp":
			case "ArrowDown":
			case "End":
			case "Home":
				break;
			case "KeyA":
			case "KeyC":
			case "KeyX":
				if (!event.metaKey && !event.ctrlKey && maxLength && target.innerText.length >= maxLength) {
					event.preventDefault();
				}
				break;
			case "KeyV":
			default:
				if (maxLength && target.innerText.length >= maxLength) {
					event.preventDefault();
				}
		}
	}

	function handleBlur(event: FocusEvent) {
		event.preventDefault();
		if (input) {
			input.scrollLeft = 0;
			input.contentEditable = "false";
		}
		removeBreak();
		value = input?.innerText;
		commitValue();
		isEditing = false;
		onBlur?.(event);
	}

	function commitValue() {
		value = value?.trim();
		if (value === originalValue) {
			return;
		}
		if (!allowEmpty && value === "") {
			resetValue();
			return;
		}
		onCommit?.(value);
		originalValue = value;
	}

	function resetValue() {
		const isChanged = value !== originalValue;
		value = originalValue;
		if (isChanged) {
			onChange?.(value);
		}
		onRevert?.(value);
	}

	export async function startEditing(highlight: boolean = false) {
		// Prevent multiple calls to startEditing jumping the cursor while typing
		if (isEditing) return;

		originalValue = value;
		isEditing = true;
		if (input) {
			input.contentEditable = "plaintext-only";
			// eslint-disable-next-line svelte/no-dom-manipulating
			input.innerText = value ?? "";
		}
		removeBreak();
		if (input && document.activeElement !== input) {
			await tick();
			input?.focus();
		}
		if (highlight) {
			selectAll(input);
		}
	}

	function handleClick(e: Event) {
		e.preventDefault();
		e.stopImmediatePropagation();
		if (input) {
			input.contentEditable = "plaintext-only";
		}
		removeBreak();
	}

	function removeBreak() {
		const node = input?.lastElementChild;
		if (node && node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === "BR") {
			// eslint-disable-next-line svelte/no-dom-manipulating
			input?.removeChild(node);
		}
	}
</script>

{#if !disabled}
	<svelte:element
		this={tag}
		bind:this={input}
		role="textbox"
		tabindex="0"
		contenteditable="false"
		{title}
		{placeholder}
		{...rest}
		class={twMerge(baseClass, focusClass, className)}
		style:--min-length="{minLength}ch"
		style:--max-length={maxLength ? `${maxLength}ch` : "100%"}
		onclick={handleClick}
		onblur={handleBlur}
		onkeydown={handleKeydown}
		onfocus={handleFocus}
		onfocusout={handleFocusout}
		oninput={handleInput}
		data-action-name={dataActionName}
	>
		{#if !isEditing}
			{value}
		{/if}
	</svelte:element>
{:else}
	<svelte:element this={tag} class={className}>{value}</svelte:element>
{/if}
